import type { TimetableGroup } from "@teamna/shared";

/**
 * Levels with the same name (prototype `teamna/prototypes/level-coaches.html`). A club made one
 * level for each coach; the Levels page offers to merge them into one.
 *
 * Names match without case and outer spaces ("Learn To swim" and "Learn To Swim"), and only levels
 * of the same kind match: a private level and a group level never merge, and neither do a level and
 * a class open to all (the server refuses that merge).
 */
export type DuplicateSet = {
  /** The level that stays: the first one, in the page's order, with the most common spelling. */
  keep: TimetableGroup;
  /** The levels that go into it. */
  others: TimetableGroup[];
  /** Every coach of the set, the kept level's first, each once. */
  staffNames: string[];
  weeklyTimes: number;
  /** The participants of the levels that go: they move to the kept level. */
  participants: number;
};

export function duplicateSets(groups: readonly TimetableGroup[]): DuplicateSet[] {
  const byKey = new Map<string, TimetableGroup[]>();
  for (const group of groups) {
    const key = `${group.kind}:${group.openToAll ? "open" : "level"}:${group.name.trim().toLowerCase()}`;
    byKey.set(key, [...(byKey.get(key) ?? []), group]);
  }
  const sets: DuplicateSet[] = [];
  for (const rows of byKey.values()) {
    if (rows.length < 2) continue;
    const spellings = new Map<string, number>();
    for (const row of rows) spellings.set(row.name.trim(), (spellings.get(row.name.trim()) ?? 0) + 1);
    const best = Math.max(...spellings.values());
    const keep = rows.find((row) => spellings.get(row.name.trim()) === best)!;
    const ordered = [keep, ...rows.filter((row) => row !== keep)];
    sets.push({
      keep,
      others: ordered.slice(1),
      staffNames: [...new Set(ordered.flatMap((row) => row.staffNames))],
      weeklyTimes: rows.reduce((total, row) => total + row.slots.length, 0),
      participants: ordered.slice(1).reduce((total, row) => total + row.participantCount, 0),
    });
  }
  return sets;
}
